'use client'

// Background-video renderer — Cloudflare Stream + Vimeo background-mode.
// All clips are pre-resolved server-side (in resolveSectionData). The client
// never sees the Stream customer code, just URL strings.
//
// Performance posture:
//   • Poster image is the LCP element — handled by the parent <HeroSection>
//     which renders <Image priority fill> alongside this component. The FIRST
//     clip therefore carries no `poster` attribute: the parent already paints
//     that same frame (HeroSections resolves its poster from
//     backgroundVideos[0].poster_url when there's no separate hero photo), and
//     the <video> sits at opacity 0 until armed, so its poster is never the
//     visible pixels. Setting it downloaded the full-width source a second time
//     — raw off media.siflyfoils.com, bypassing next/image, ~229 KB on the
//     Adventure Elevated hero. Sibling clips keep their poster: nothing else
//     paints their first frame during the crossfade.
//   • <video> / <iframe> mount empty; sources get injected only after IO
//     intersection (rootMargin 200px). Once playing, a second IO toggles
//     pause/play so off-screen clips don't burn battery.
//   • prefers-reduced-motion, navigator.connection.saveData,
//     effectiveType ∈ {slow-2g,2g}, and < 768px viewport (when
//     enable_on_mobile is off) all short-circuit to poster-only.
//   • Multi-clip cycling: clips stack with absolute positioning, the
//     active one fades in via Tailwind opacity transition. CF Stream
//     advances on `ended`; Vimeo advances on a fixed-duration timer
//     (vimeo_clip_duration_seconds option, default 10).
//   • Scroll-driven scrubbing: CF-only. video.currentTime tracks
//     section progress through viewport inside requestAnimationFrame.
//
// Decorative content — `aria-hidden="true"` on the video element. A
// manual play/pause overlay button is the accessible affordance; we
// only render it when `manual_pause_button` option is true.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ResolvedBackgroundVideo } from '../ContentSections'

// Pinned HLS rendition ceiling. We pick the largest rendition ≤ this height and
// hold it (no ABR) so a decorative background loop renders at a consistent
// quality from the first frame and never degrades when it loops. ABR's
// bandwidth-saving buys little on a fixed-size muted background but its
// ramp-up — and the cached low-quality opening replayed on every loop — reads
// as a polish defect. The ceiling is author-selectable per hero via the
// `max_resolution` option (720 | 1080). 720p roughly halves the bytes a visitor
// loads (~28 MB vs ~50 MB for a 62s clip) at negligible cost on a full-bleed
// background, so it's the default. The cap also tames a 4K/1440p source; a
// source below the cap just pins to its own top rung. The pin only applies on
// the hls.js path; the native-HLS fallback (no-MSE browsers) self-ABRs.
const DEFAULT_MAX_HEIGHT = 720

type Props = {
  clips: ResolvedBackgroundVideo[]
  /** Shared options blob, sourced from clips[0].options server-side. */
  options?: Record<string, unknown>
  /** First-frame fallback poster (e.g. hero photo). Rendered by parent. */
  className?: string
}

const MOBILE_BREAKPOINT = 768

function optBool(opts: Record<string, unknown> | undefined, key: string, fallback = false): boolean {
  return typeof opts?.[key] === 'boolean' ? Boolean(opts[key]) : fallback
}
function optNum(opts: Record<string, unknown> | undefined, key: string, fallback: number): number {
  return typeof opts?.[key] === 'number' ? Number(opts[key]) : fallback
}
function optStr(opts: Record<string, unknown> | undefined, key: string, fallback: string): string {
  return typeof opts?.[key] === 'string' ? String(opts[key]) : fallback
}

export function BackgroundVideo({ clips, options }: Props) {
  const [shouldRender, setShouldRender] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const [manuallyPaused, setManuallyPaused] = useState(false)
  const [armed, setArmed] = useState(false) // intersection-observed once
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const iframeRefs = useRef<Array<HTMLIFrameElement | null>>([])

  const enableOnMobile = optBool(options, 'enable_on_mobile', false)
  const overlayOpacity = Math.max(0, Math.min(1, optNum(options, 'overlay_opacity', 0.4)))
  const overlayGradient = optStr(options, 'overlay_gradient', 'none')
  const maxHeight = optNum(options, 'max_resolution', DEFAULT_MAX_HEIGHT)
  const enableScrollScrub = optBool(options, 'enable_scroll_scrub', false)
  const crossfadeSeconds = optNum(options, 'cycle_crossfade_seconds', 1)
  const manualPauseButton = optBool(options, 'manual_pause_button', false)
  const vimeoClipDurationSeconds = optNum(options, 'vimeo_clip_duration_seconds', 10)

  // Capability gate — runs once on mount. If any reason to skip, we never
  // enter the IO-arm path and the poster (rendered by the parent) is the
  // only content the user ever sees.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Network Information API (navigator.connection) is not in the TS DOM lib
    const conn = (navigator as any).connection
    if (conn?.saveData) return
    const eff = conn?.effectiveType as string | undefined
    if (eff === 'slow-2g' || eff === '2g') return
    if (window.innerWidth < MOBILE_BREAKPOINT && !enableOnMobile) return
    setShouldRender(true)
  }, [enableOnMobile])

  // Arm-on-first-intersect (rootMargin 200px). Pattern lifted from
  // SocialEmbedSection's embed gate.
  useEffect(() => {
    if (!shouldRender || armed) return
    const node = containerRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setArmed(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setArmed(true)
          io.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [shouldRender, armed])

  // Pause/resume on viewport visibility (no rootMargin so we pause as soon
  // as the section actually leaves viewport — keeps GPU/CPU off long pages).
  useEffect(() => {
    if (!armed || manuallyPaused) return
    const node = containerRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting)
      const video = videoRefs.current[activeIdx]
      const iframe = iframeRefs.current[activeIdx]
      if (visible) {
        video?.play().catch(() => undefined)
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*')
        }
      } else {
        video?.pause()
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*')
        }
      }
    })
    io.observe(node)
    return () => io.disconnect()
  }, [armed, activeIdx, manuallyPaused])

  // Cycling: advance on CF Stream `ended` event or fixed timer for Vimeo.
  useEffect(() => {
    if (!armed || clips.length < 2 || manuallyPaused) return
    const active = clips[activeIdx]
    if (!active) return
    if (active.source === 'cloudflare_stream') {
      const video = videoRefs.current[activeIdx]
      if (!video) return
      const onEnded = () => setActiveIdx((i) => (i + 1) % clips.length)
      video.addEventListener('ended', onEnded)
      return () => video.removeEventListener('ended', onEnded)
    }
    // Vimeo — fixed timer.
    const ms = Math.max(2000, vimeoClipDurationSeconds * 1000)
    const t = setTimeout(() => setActiveIdx((i) => (i + 1) % clips.length), ms)
    return () => clearTimeout(t)
  }, [armed, activeIdx, clips, manuallyPaused, vimeoClipDurationSeconds])

  // HLS attach — active CF clip only. Lazy-imports hls.js so the lib (~30KB
  // gzipped) only ships once we've armed playback.
  //
  // We force hls.js wherever MediaSource is supported — Chrome / Firefox / Edge
  // AND Safari 17+ / iOS 17.1+ — so the rendition pin below applies and the clip
  // renders at one consistent quality instead of ABR-ramping up from a low start.
  // We deliberately do NOT defer to native HLS just because the browser advertises
  // it (`canPlayType('application/vnd.apple.mpegurl')` returns "maybe" on Safari
  // and on some Chromium builds): native HLS gives no programmatic rendition
  // control, so it's the ramp we're trying to kill. Native HLS is used only as a
  // fallback when MSE is unavailable (older iOS Safari), where it does its own
  // ABR — we accept the ramp there. The Stream MP4 download isn't generated (404),
  // so it is never used.
  useEffect(() => {
    if (!armed) return
    const active = clips[activeIdx]
    if (!active || active.source !== 'cloudflare_stream') return
    const video = videoRefs.current[activeIdx]
    if (!video) return

    let hls: { destroy: () => void } | null = null
    let cancelled = false

    // Native-HLS fallback (browser handles ABR itself). Only used when MSE is
    // unavailable — i.e. hls.js can't run.
    const attachNative = () => {
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = active.hls_url
      }
    }

    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return
      if (!Hls.isSupported()) {
        attachNative()
        return
      }
      const instance = new Hls({ capLevelToPlayerSize: false })
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        // Pick the rendition ≤ the selected cap (maxHeight) with the largest
        // height. If everything is above the cap, take the smallest rendition.
        const levels = instance.levels
        if (!levels || levels.length === 0) return
        let pick = -1
        let pickHeight = 0
        let smallestIdx = 0
        let smallestHeight = Infinity
        for (let i = 0; i < levels.length; i++) {
          const h = levels[i].height ?? 0
          if (h <= maxHeight && h > pickHeight) { pick = i; pickHeight = h }
          if (h > 0 && h < smallestHeight) { smallestIdx = i; smallestHeight = h }
        }
        // Pin the level (currentLevel, not startLevel) — this disables ABR,
        // so the chosen rendition is fetched from the first segment and held
        // across loops. No ramp-up, no low-quality opening on replay.
        instance.currentLevel = pick >= 0 ? pick : smallestIdx
      })
      instance.loadSource(active.hls_url)
      instance.attachMedia(video)
      hls = instance
    }).catch(() => {
      // hls.js failed to load — native HLS is the only remaining option.
      if (!cancelled) attachNative()
    })

    return () => {
      cancelled = true
      if (hls) hls.destroy()
      // Clearing src lets the next clip's effect re-attach cleanly without
      // the previous source still trying to buffer in the background.
      video.removeAttribute('src')
      video.load()
    }
  }, [armed, activeIdx, clips, maxHeight])

  // Scroll-scrub (CF Stream only). Active clip's currentTime tracks
  // section progress through viewport.
  useEffect(() => {
    if (!armed || !enableScrollScrub) return
    const active = clips[activeIdx]
    if (!active || active.source !== 'cloudflare_stream') return
    const video = videoRefs.current[activeIdx]
    const node = containerRef.current
    if (!video || !node) return

    let raf = 0
    const tick = () => {
      const rect = node.getBoundingClientRect()
      const winH = window.innerHeight
      // Section progress: 0 = section bottom at top of viewport, 1 = section top at bottom of viewport.
      const total = rect.height + winH
      const scrolled = winH - rect.top
      const progress = Math.max(0, Math.min(1, scrolled / total))
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = progress * video.duration
      }
      raf = requestAnimationFrame(tick)
    }
    // Pause normal playback so currentTime writes don't fight with the engine.
    video.pause()
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [armed, enableScrollScrub, activeIdx, clips])

  const overlayClass = useMemo(() => {
    if (overlayGradient === 'top') return 'bg-gradient-to-b from-black to-transparent'
    if (overlayGradient === 'bottom') return 'bg-gradient-to-t from-black to-transparent'
    if (overlayGradient === 'radial') return 'bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,1))]'
    return 'bg-black'
  }, [overlayGradient])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
    >
      {clips.map((clip, idx) => {
        const isActive = idx === activeIdx
        const opacity = armed && isActive && !manuallyPaused ? 1 : 0
        if (clip.source === 'cloudflare_stream') {
          return (
            <video
              key={idx}
              ref={(el) => { videoRefs.current[idx] = el }}
              muted
              playsInline
              loop={clips.length === 1}
              autoPlay={armed && isActive && !enableScrollScrub}
              poster={idx === 0 ? undefined : clip.poster_url}
              preload={armed ? 'auto' : 'none'}
              className="absolute inset-0 w-full h-full object-cover transition-opacity"
              style={{ opacity, transitionDuration: `${crossfadeSeconds}s` }}
            />
          )
        }
        // Vimeo
        return (
          <iframe
            key={idx}
            ref={(el) => { iframeRefs.current[idx] = el }}
            src={armed && isActive ? clip.iframe_src : 'about:blank'}
            className="absolute inset-0 w-full h-full transition-opacity"
            style={{ opacity, transitionDuration: `${crossfadeSeconds}s`, border: 0 }}
            allow="autoplay; fullscreen; picture-in-picture"
            title="Background video"
          />
        )
      })}
      {overlayOpacity > 0 && (
        <div
          className={`absolute inset-0 ${overlayClass} pointer-events-none`}
          style={{ opacity: overlayOpacity }}
        />
      )}
      {manualPauseButton && armed && (
        <button
          type="button"
          aria-pressed={manuallyPaused}
          aria-label={manuallyPaused ? 'Resume background video' : 'Pause background video'}
          onClick={(e) => {
            e.stopPropagation()
            setManuallyPaused((p) => !p)
            const video = videoRefs.current[activeIdx]
            if (video) {
              if (manuallyPaused) video.play().catch(() => undefined)
              else video.pause()
            }
          }}
          className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/50 text-white text-xs backdrop-blur-sm pointer-events-auto hover:bg-black/70"
        >
          {manuallyPaused ? 'Play' : 'Pause'}
        </button>
      )}
    </div>
  )
}
